import { Component, OnDestroy, OnInit, AfterViewInit, HostListener, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { SafeResourceUrl } from '@angular/platform-browser';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { Subscription, timer } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';

import { OsintSearchService } from '../../../services/osint-search/osint-search.service';
import { GoogleCseService } from '../../../services/google-cse/google-cse.service';
import {
  GoogleOsintQueryBuilder,
  GoogleOsintQueryVariant,
} from '../../../services/google-cse/google-osint-query.builder';
import { isOsintJobVendorSyncComplete, isBackgroundRefreshPending, isVendorSyncComplete, toPercent } from '../../../services/search-polling/osint-job-poll-response.util';
import { SearchPollingService } from '../../../services/search-polling/search-polling.service';
import { SearchServiceStatusService } from '../../../services/search-service-status/search-service-status.service';
import { resolvePlatformIconVisual, createPlatformIconDataUrl } from '../../../services/link-analysis/platform-icon.util';
import {
  SearchResultProcessorService,
  SearchImage,
  DEFAULT_PROFILE_AVATAR_SRC,
} from '../../../services/search-result-processor/search-result-processor.service';
import {
  canStartImageSearch as canStartImageSearchUtil,
  getFaceDetectCache,
  setFaceDetectCache,
  hasFaceSearchPermission,
  isFaceDetectionPending,
  isFaceDetectionReady,
  navigateToOsintImageSearch,
  resolveImageSearchSrc,
  shouldSoftPollFaceDetection,
  type ImageSearchCandidate,
} from '../../../services/osint-search/image-search-from-result.util';
import { UtilsService } from '../../../services/utils/utils.service';
import { deniesFieldSearchIcon } from '../../../utils/canonical-field-key';
import { OsintAiAnalysisComponent } from '../osint-ai-analysis/osint-ai-analysis.component';
import { openOsintPrintableDialog } from '../osint-search-printable/osint-search-printable.dialog';
import { Gallery, GalleryItem, ImageItem } from 'ng-gallery';
import { Lightbox } from 'ng-gallery/lightbox';

@Component({
  selector: 'app-osint-username-search-result',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    OsintAiAnalysisComponent,
  ],
  templateUrl: './osint-username-search-result.component.html',
  styleUrl: './osint-username-search-result.component.css',
})
export class OsintUsernameSearchResultComponent
  implements OnInit, AfterViewInit, OnDestroy {
  items: GalleryItem[] = [];
  jobId: string = '';
  searchId: string = '';
  noRecords = false;
  selectedSearchResult: any;

  searchResultImages: SearchImage[] = [];
  searchResultDetails: any[] = [];
  searchResultDetailsFiltered: any[] = [];
  aggregatedNames: string[] = [];
  aggregatedEmails: string[] = [];
  aggregatedEmailHints: string[] = [];
  loadingCompletedSearchResult: boolean = false;
  loadingPolledSearchResult: boolean = false;

  jobResultSubscription?: Subscription;
  private vendorSyncSubscription?: Subscription;
  private faceDetectionRefreshSubscription?: Subscription;
  private masonryRafId: number | null = null;
  private masonryResizeObserver: ResizeObserver | null = null;
  private readonly masonryGapPx = 18; // ~1.15rem

  get isLoadingResults(): boolean {
    const hasResults =
      (this.searchResultDetails?.length > 0 ||
        this.searchResultImages?.length > 0) &&
      !this.loadingImages;
    return (
      (this.loadingCompletedSearchResult || this.loadingPolledSearchResult) &&
      !hasResults
    );
  }

  /** True while vendor search is still polling / refreshing partial results. */
  get isResultStillPolling(): boolean {
    const row = this.selectedSearchResult;
    if (!row) return false;
    if (this.isResultPollError || this.isResultDeleted) {
      return false;
    }
    if (isVendorSyncComplete(row)) {
      return false;
    }
    return (
      isBackgroundRefreshPending(row) ||
      !row.done ||
      row.vendor_sync_complete === false
    );
  }

  get isResultPollError(): boolean {
    const row = this.selectedSearchResult;
    return (
      !!row &&
      (row.timed_out === true ||
        row.status === 'error' ||
        row.status === 'failed')
    );
  }

  get isResultDeleted(): boolean {
    const row = this.selectedSearchResult;
    return !!row && (row.deleted === true || row.status === 'deleted');
  }

  /** Label shown after "Fetching" on the result page chip. */
  get resultProgressPercentLabel(): string {
    if (this.isResultPollError || this.isResultDeleted) {
      return '';
    }
    const percent = this.resolveSelectedProgressPercent();
    return percent != null ? `${percent}%` : '…';
  }

  private resolveSelectedProgressPercent(): number | null {
    const row = this.selectedSearchResult;
    if (!row) return null;
    // Prefer original 3rd-party progress.percent whenever present.
    return this.resolveProgressNumber(
      row.progress?.percent,
      row.progress_percent,
      row.extra_data?.progress?.percent,
      row.progress_milestone,
      row.progress?.milestone,
      row.extra_data?.progress?.milestone,
    );
  }

  private resolveProgressNumber(...values: any[]): number | null {
    return toPercent(...values);
  }

  /** Floor progress into 5% buckets: 0, 5, 10, …, 100. */
  private toProgressBucket(percent: number | null): number | null {
    if (percent == null || !Number.isFinite(percent)) {
      return null;
    }
    return Math.floor(Math.min(100, Math.max(0, percent)) / 5) * 5;
  }

  /**
   * Prefer backend `categories` list; otherwise collect unique category
   * values from results. Always keeps "All" first.
   */
  private applyPlatformCategoriesFromResponse(data: any): void {
    const fromApi = Array.isArray(data?.categories)
      ? data.categories
        .map((c: any) => String(c || '').trim())
        .filter(Boolean)
      : [];

    const collected: string[] = [];
    const seen = new Set<string>();

    const pushUnique = (raw: string) => {
      const label = String(raw || '').trim();
      if (!label) return;
      const key = label.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      collected.push(label);
    };

    if (fromApi.length) {
      fromApi.forEach(pushUnique);
    } else {
      const results = Array.isArray(data?.results) ? data.results : [];
      for (const item of results) {
        pushUnique(item?.category);
      }
      // Legacy / missing category: derive from platform name map
      if (!collected.length) {
        for (const item of results) {
          pushUnique(this.getPlatformCategory(item?.platform || item?.name));
        }
      }
    }

    collected.sort((a, b) => {
      const aOther = a.toLowerCase() === 'other';
      const bOther = b.toLowerCase() === 'other';
      if (aOther && !bOther) return 1;
      if (!aOther && bOther) return -1;
      return a.localeCompare(b);
    });
    this.platformCategoryTabs = ['All', ...collected];

    if (
      this.selectedPlatformCategory !== 'All' &&
      !collected.some(
        (c) =>
          c.toLowerCase() === this.selectedPlatformCategory.toLowerCase(),
      )
    ) {
      this.selectedPlatformCategory = 'All';
    }
  }

  /** Keep polling in the root service until vendor sync completes (survives navigation). */
  private ensureUsernameJobPolling(jobId: string) {
    if (!jobId) return;
    this.searchPollingService.ensureBackgroundPolling(
      [
        {
          job_id: jobId,
          searchType: 'username',
          label:
            this.selectedSearchResult?.searched_username ||
            this.selectedSearchResult?.query_value ||
            jobId,
        },
      ],
      3 * 1000,
    );
  }

  private markResultPollComplete(status?: any) {
    const deleted =
      status?.deleted === true || status?.status === 'deleted';
    const timedOut =
      status?.timed_out === true || status?.status === 'error';
    const progressPercent = toPercent(
      status?.progress?.percent,
      status?.progress_percent,
      this.selectedSearchResult?.progress_percent,
    );

    this.selectedSearchResult = {
      ...this.selectedSearchResult,
      done: true,
      deleted,
      timed_out: timedOut,
      status: deleted
        ? 'deleted'
        : timedOut
          ? 'error'
          : status?.status === 'failed'
            ? 'completed'
            : 'completed',
      progress_percent: progressPercent,
      progress_milestone:
        status?.progress_milestone ??
        this.selectedSearchResult?.progress_milestone,
      progress: status?.progress ?? this.selectedSearchResult?.progress,
      emails_ready:
        status?.emails_ready ?? this.selectedSearchResult?.emails_ready,
      vendor_sync_complete: true,
      is_final: true,
      background_refresh_pending: false,
    };
    this.jobResultSubscription?.unsubscribe();
    this.vendorSyncSubscription?.unsubscribe();
  }

  private applyPollStatusToSelectedResult(status: any) {
    if (!status) return;
    const deleted =
      status.deleted === true || status.status === 'deleted';
    const timedOut =
      status.timed_out === true || status.status === 'error';
    const vendorComplete = isOsintJobVendorSyncComplete(status);
    const progressPercent = toPercent(
      status?.progress?.percent,
      status.progress_percent,
    );

    this.selectedSearchResult = {
      ...this.selectedSearchResult,
      done: deleted || timedOut
        ? true
        : status.done || this.selectedSearchResult?.done || false,
      deleted,
      timed_out: timedOut,
      status: deleted
        ? 'deleted'
        : timedOut
          ? 'error'
          : vendorComplete
            ? 'completed'
            : status.status || 'pending',
      progress_percent: progressPercent,
      progress_milestone: status.progress_milestone,
      progress: status.progress ?? this.selectedSearchResult?.progress,
      emails_ready: Boolean(status.emails_ready),
      vendor_sync_complete: vendorComplete,
      is_final: vendorComplete || Boolean(status.is_final),
      background_refresh_pending: deleted || timedOut ? false : !vendorComplete,
    };
  }

  searchTerm: string = '';
  notes: string = '';
  loadingImages: boolean = false;

  showGoogleResults: boolean = true;
  googleQueryVariants: GoogleOsintQueryVariant[] = [];
  selectedGoogleQueryId: string | null = null;
  googleResultsUrl: SafeResourceUrl | null = null;
  googleSearchHref: string | null = null;
  selectedImage: SearchImage | null = null;
  hasImageSearchPermission = hasFaceSearchPermission();
  private processedIcons: WeakSet<HTMLImageElement> = new WeakSet();
  showFoundDrillModal: boolean = false;
  foundPlatformSearchTerm: string = '';
  showNamesDrillModal: boolean = false;
  namesSearchTerm: string = '';
  showPhotosDrillModal: boolean = false;
  showEmailsDrillModal: boolean = false;
  emailsSearchTerm: string = '';
  showEmailHintsDrillModal: boolean = false;
  emailHintsSearchTerm: string = '';
  showUnknownDrillModal: boolean = false;
  unknownPlatformSearchTerm: string = '';
  showNotFoundDrillModal: boolean = false;
  notFoundPlatformSearchTerm: string = '';
  photoDrillShapes: Record<number, 'portrait' | 'landscape' | 'square'> = {};
  selectedPlatformCategory: string = 'All';
  showFoundOnly: boolean = false;
  isAtTop: boolean = true;
  private scrollHostListener: (() => void) | null = null;
  /** Built from API `categories` (plus "All"); falls back to collecting from results. */
  platformCategoryTabs: string[] = ['All'];
  summaryCollapsed = {
    names: true,
    emails: true,
    emailHints: true,
    photos: true,
  };
  activeViewTab: 'results' | 'ai-analysis' = 'results';
  private readonly collagePreviewLimit = 5;

  get isOsintAiAnalysisEnabled(): boolean {
    return this.searchServiceStatusService.isOsintAiAnalysisEnabled();
  }

  get collagePreviewImages(): SearchImage[] {
    return (this.searchResultImages ?? []).slice(0, this.collagePreviewLimit);
  }

  get remainingPhotosCount(): number {
    return Math.max(
      0,
      (this.searchResultImages?.length ?? 0) - this.collagePreviewLimit,
    );
  }

  get collageHeroImage(): SearchImage | null {
    return this.collagePreviewImages[0] ?? null;
  }

  get collageTopImages(): SearchImage[] {
    return this.collagePreviewImages.slice(1, 3);
  }

  get collageBottomImages(): SearchImage[] {
    return this.collagePreviewImages.slice(3, 5);
  }

  get usesTwoColumnSourceCards(): boolean {
    return this.displayedSearchResultDetails.length === 2;
  }

  get displayedSearchResultDetails(): any[] {
    let list = this.searchResultDetailsFiltered ?? [];
    if (this.showFoundOnly) {
      list = list.filter(
        (platform) =>
          String(platform?.status || '').toLowerCase() === 'found' ||
          (!platform?.notFound && !platform?.isUnknown),
      );
    }
    if (this.selectedPlatformCategory !== 'All') {
      list = list.filter(
        (platform) =>
          this.resolvePlatformCategory(platform) ===
          this.selectedPlatformCategory,
      );
    }
    return [...list].sort(
      (a, b) =>
        this.getPlatformDisplayRank(a) - this.getPlatformDisplayRank(b),
    );
  }

  private getPlatformDisplayRank(platform: any): number {
    if (platform?.notFound) {
      return 4;
    }
    if (platform?.isUnknown) {
      return 3;
    }
    if (this.platformHasPhotoData(platform)) {
      return 0;
    }
    if (this.platformHasProfileData(platform)) {
      return 1;
    }
    return 2;
  }

  private platformHasPhotoData(platform: any): boolean {
    if (!platform || platform.notFound || platform.isUnknown) {
      return false;
    }
    if (this.getPlatformImageForCard(platform)?.src) {
      return true;
    }
    if (String(platform.imageUrl || '').trim()) {
      return true;
    }
    const profile = platform.profile;
    if (profile && typeof profile === 'object') {
      return Boolean(
        String(profile.avatar_full || profile.avatar || '').trim(),
      );
    }
    return false;
  }

  private platformHasProfileData(platform: any): boolean {
    if (
      !platform ||
      platform.notFound ||
      platform.isUnknown ||
      platform.emptyAccount
    ) {
      return false;
    }
    if (this.getCardDescription(platform)) {
      return true;
    }
    return (this.getCardBodyDetails(platform) ?? []).some(
      (detail) => !this.isEmptyValue(detail?.value),
    );
  }

  selectPlatformCategory(category: string): void {
    this.selectedPlatformCategory = category;
    setTimeout(() => {
      this.scheduleMasonryLayout();
      this.scrollToResultSection('source-results');
    }, 0);
  }

  toggleFoundOnly(): void {
    this.showFoundOnly = !this.showFoundOnly;
    this.scheduleMasonryLayout();
  }

  jumpToTop(): void {
    const host = document
      .querySelector('.search-container')
      ?.closest('.h-100p.overflow-y-scroll') as HTMLElement | null;
    if (host) {
      host.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    this.isAtTop = true;
  }

  jumpToGoogleResults(): void {
    this.scrollToResultSection('google-results');
  }

  private setupFilterScrollListener(): void {
    this.teardownFilterScrollListener();
    const host = document
      .querySelector('.search-container')
      ?.closest('.h-100p.overflow-y-scroll') as HTMLElement | null;
    const onScroll = () => {
      const top = host ? host.scrollTop : window.scrollY;
      this.isAtTop = top < 180;
    };
    onScroll();
    if (host) {
      host.addEventListener('scroll', onScroll, { passive: true });
      this.scrollHostListener = () =>
        host.removeEventListener('scroll', onScroll);
    } else {
      window.addEventListener('scroll', onScroll, { passive: true });
      this.scrollHostListener = () =>
        window.removeEventListener('scroll', onScroll);
    }
  }

  private teardownFilterScrollListener(): void {
    this.scrollHostListener?.();
    this.scrollHostListener = null;
  }

  /** Vendor scan stats from /osint/results (3rd-party shape). */
  get vendorStats(): {
    total_checked?: number | null;
    found?: number | null;
    not_found?: number | null;
    unknown?: number | null;
  } | null {
    const row = this.selectedSearchResult;
    return row?.stats || row?.vendor?.stats || row?.extra_data?.stats || null;
  }

  get foundCount(): number {
    const fromStats = Number(this.vendorStats?.found);
    if (Number.isFinite(fromStats) && fromStats >= 0) {
      return fromStats;
    }
    return (this.searchResultDetails ?? []).filter(
      (detail) =>
        String(detail?.status || '').toLowerCase() === 'found' ||
        (!detail?.notFound && !detail?.isUnknown),
    ).length;
  }

  get notFoundCount(): number {
    const fromStats = Number(this.vendorStats?.not_found);
    if (Number.isFinite(fromStats) && fromStats >= 0) {
      return fromStats;
    }
    return (this.searchResultDetails ?? []).filter(
      (detail) =>
        String(detail?.status || '').toLowerCase() === 'not_found' ||
        !!detail?.notFound,
    ).length;
  }

  get unknownCount(): number {
    const fromStats = Number(this.vendorStats?.unknown);
    if (Number.isFinite(fromStats) && fromStats >= 0) {
      return fromStats;
    }
    return this.unknownPlatforms.length;
  }

  get unknownPlatforms(): any[] {
    const row = this.selectedSearchResult;
    const fromResults = row?.results;
    if (Array.isArray(fromResults)) {
      return fromResults.filter(
        (item) => String(item?.status || '').toLowerCase() === 'unknown',
      );
    }
    const fromDetails = (this.searchResultDetails ?? []).filter(
      (detail) =>
        String(detail?.status || '').toLowerCase() === 'unknown' ||
        !!detail?.isUnknown,
    );
    if (fromDetails.length) {
      return fromDetails;
    }
    const list =
      row?.unknown_platforms || row?.extra_data?.unknown_platforms || [];
    return Array.isArray(list) ? list : [];
  }

  get filteredUnknownPlatforms(): any[] {
    const term = this.unknownPlatformSearchTerm.trim().toLowerCase();
    const list = this.unknownPlatforms;
    if (!term) {
      return list;
    }
    return list.filter((platform) =>
      this.matchesDrillPlatformSearch(platform, term),
    );
  }

  get notFoundPlatforms(): any[] {
    const fromDetails = (this.searchResultDetails ?? []).filter(
      (detail) =>
        String(detail?.status || '').toLowerCase() === 'not_found' ||
        !!detail?.notFound,
    );
    if (fromDetails.length) {
      return fromDetails;
    }
    const fromResults = this.selectedSearchResult?.results;
    if (Array.isArray(fromResults)) {
      return fromResults.filter(
        (item) => String(item?.status || '').toLowerCase() === 'not_found',
      );
    }
    return [];
  }

  get filteredNotFoundPlatforms(): any[] {
    const term = this.notFoundPlatformSearchTerm.trim().toLowerCase();
    const list = this.notFoundPlatforms;
    if (!term) {
      return list;
    }
    return list.filter((platform) =>
      this.matchesDrillPlatformSearch(platform, term),
    );
  }

  get namesCount(): number {
    return this.aggregatedNames?.length ?? 0;
  }

  get photosCount(): number {
    return this.searchResultImages?.length ?? 0;
  }

  get emailsCount(): number {
    return this.aggregatedEmails?.length ?? 0;
  }

  get emailHintsCount(): number {
    return this.aggregatedEmailHints?.length ?? 0;
  }

  get filteredEmails(): string[] {
    const term = this.emailsSearchTerm.trim().toLowerCase();
    const emails = this.aggregatedEmails ?? [];
    if (!term) {
      return emails;
    }
    return emails.filter((email) =>
      String(email || '').toLowerCase().includes(term),
    );
  }

  get filteredEmailHints(): string[] {
    const term = this.emailHintsSearchTerm.trim().toLowerCase();
    const hints = this.aggregatedEmailHints ?? [];
    if (!term) {
      return hints;
    }
    return hints.filter((hint) =>
      String(hint || '').toLowerCase().includes(term),
    );
  }

  get foundPlatforms(): any[] {
    return (this.searchResultDetails ?? []).filter(
      (detail) =>
        String(detail?.status || '').toLowerCase() === 'found' ||
        (!detail?.notFound && !detail?.isUnknown),
    );
  }

  get filteredFoundPlatforms(): any[] {
    const term = this.foundPlatformSearchTerm.trim().toLowerCase();
    if (!term) {
      return this.foundPlatforms;
    }
    return this.foundPlatforms.filter((platform) =>
      this.matchesDrillPlatformSearch(platform, term),
    );
  }

  get filteredNames(): string[] {
    const term = this.namesSearchTerm.trim().toLowerCase();
    const names = this.aggregatedNames ?? [];
    if (!term) {
      return names;
    }
    return names.filter((name) => String(name || '').toLowerCase().includes(term));
  }

  get filteredNameDrillEntries(): Array<{
    name: string;
    platforms: any[];
  }> {
    const term = this.namesSearchTerm.trim().toLowerCase();
    const entries = (this.aggregatedNames ?? []).map((name) => ({
      name,
      platforms: this.getPlatformsForName(name),
    }));
    if (!term) {
      return entries;
    }
    return entries.filter((entry) => {
      if (String(entry.name || '').toLowerCase().includes(term)) {
        return true;
      }
      return entry.platforms.some((platform) => {
        const platformName = String(platform?.platform || '').toLowerCase();
        const category = this.getPlatformCategory(platform?.platform).toLowerCase();
        return platformName.includes(term) || category.includes(term);
      });
    });
  }

  getPlatformsForName(name: string): any[] {
    const needle = String(name || '')
      .trim()
      .toLowerCase();
    if (!needle) {
      return [];
    }

    return (this.foundPlatforms ?? []).filter((platform) => {
      const displayName = this.getFoundPlatformDisplayName(platform)
        .trim()
        .toLowerCase();
      if (displayName && displayName === needle) {
        return true;
      }

      return this.getFlattenedDetails(platform).some((detail) => {
        const label = String(detail?.label || '')
          .toLowerCase()
          .trim();
        const isNameField =
          label === 'name' ||
          label === 'display name' ||
          label === 'full name' ||
          label === 'title' ||
          label.includes('name');
        if (!isNameField || this.isEmptyValue(detail?.value)) {
          return false;
        }
        const value = this.utilsService
          .formatDisplayValue(detail.value)
          .trim()
          .toLowerCase();
        return value === needle;
      });
    });
  }

  getNameDrillPrimaryPlatform(platforms: any[]): any | null {
    return platforms?.[0] ?? null;
  }

  getNameDrillThumb(platforms: any[]): string {
    const primary = this.getNameDrillPrimaryPlatform(platforms);
    if (primary) {
      return this.getFoundPlatformThumb(primary);
    }
    return 'assets/icons/default.png';
  }

  getNameDrillPlatformIcon(platforms: any[]): string {
    const primary = this.getNameDrillPrimaryPlatform(platforms);
    return primary?.icon || 'assets/icons/default.png';
  }

  getNameDrillSubtitle(platforms: any[]): string {
    if (!platforms?.length) {
      return 'Source unknown';
    }
    const primary = platforms[0];
    const platformName = String(primary?.platform || '').trim() || 'Unknown';
    const category = this.getPlatformCategory(platformName);
    if (platforms.length === 1) {
      return category ? `${platformName} · ${category}` : platformName;
    }
    return `${platformName} +${platforms.length - 1} more · ${category}`;
  }

  onNameDrillClick(platforms: any[]): void {
    const primary = this.getNameDrillPrimaryPlatform(platforms);
    this.closeNamesDrillModal();
    setTimeout(() => {
      if (!primary) {
        this.scrollToResultSection('names-summary');
        return;
      }
      const platformKey = this.normalizePlatformNameForLookup(primary?.platform);
      const element = document.getElementById(`platform-section-${platformKey}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        this.scrollToResultSection('source-results');
      }
    }, 50);
  }

  scrollToResultSection(sectionId: string): void {
    const element = document.getElementById(sectionId);
    if (!element) {
      return;
    }
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private syncDrillModalBodyScroll(): void {
    document.body.style.overflow =
      this.showFoundDrillModal ||
        this.showNamesDrillModal ||
        this.showPhotosDrillModal ||
        this.showEmailsDrillModal ||
        this.showEmailHintsDrillModal ||
        this.showUnknownDrillModal ||
        this.showNotFoundDrillModal
        ? 'hidden'
        : '';
  }

  openFoundDrillModal(): void {
    this.closeNamesDrillModal();
    this.closePhotosDrillModal();
    this.closeEmailsDrillModal();
    this.closeEmailHintsDrillModal();
    this.closeUnknownDrillModal();
    this.closeNotFoundDrillModal();
    this.foundPlatformSearchTerm = '';
    this.showFoundDrillModal = true;
    this.syncDrillModalBodyScroll();
  }

  closeFoundDrillModal(): void {
    this.showFoundDrillModal = false;
    this.foundPlatformSearchTerm = '';
    this.syncDrillModalBodyScroll();
  }

  openNamesDrillModal(): void {
    this.closeFoundDrillModal();
    this.closePhotosDrillModal();
    this.closeEmailsDrillModal();
    this.closeEmailHintsDrillModal();
    this.closeUnknownDrillModal();
    this.closeNotFoundDrillModal();
    this.namesSearchTerm = '';
    this.showNamesDrillModal = true;
    this.syncDrillModalBodyScroll();
  }

  closeNamesDrillModal(): void {
    this.showNamesDrillModal = false;
    this.namesSearchTerm = '';
    this.syncDrillModalBodyScroll();
  }

  openPhotosDrillModal(): void {
    if (!this.photosCount) {
      return;
    }
    this.closeFoundDrillModal();
    this.closeNamesDrillModal();
    this.closeEmailsDrillModal();
    this.closeEmailHintsDrillModal();
    this.closeUnknownDrillModal();
    this.closeNotFoundDrillModal();
    this.photoDrillShapes = {};
    this.showPhotosDrillModal = true;
    this.syncDrillModalBodyScroll();
  }

  closePhotosDrillModal(): void {
    this.showPhotosDrillModal = false;
    this.syncDrillModalBodyScroll();
  }

  openEmailsDrillModal(): void {
    if (!this.emailsCount) {
      return;
    }
    this.closeFoundDrillModal();
    this.closeNamesDrillModal();
    this.closePhotosDrillModal();
    this.closeEmailHintsDrillModal();
    this.closeUnknownDrillModal();
    this.closeNotFoundDrillModal();
    this.emailsSearchTerm = '';
    this.showEmailsDrillModal = true;
    this.syncDrillModalBodyScroll();
  }

  closeEmailsDrillModal(): void {
    this.showEmailsDrillModal = false;
    this.emailsSearchTerm = '';
    this.syncDrillModalBodyScroll();
  }

  openEmailHintsDrillModal(): void {
    if (!this.emailHintsCount) {
      return;
    }
    this.closeFoundDrillModal();
    this.closeNamesDrillModal();
    this.closePhotosDrillModal();
    this.closeEmailsDrillModal();
    this.closeUnknownDrillModal();
    this.closeNotFoundDrillModal();
    this.emailHintsSearchTerm = '';
    this.showEmailHintsDrillModal = true;
    this.syncDrillModalBodyScroll();
  }

  closeEmailHintsDrillModal(): void {
    this.showEmailHintsDrillModal = false;
    this.emailHintsSearchTerm = '';
    this.syncDrillModalBodyScroll();
  }

  openUnknownDrillModal(): void {
    this.closeFoundDrillModal();
    this.closeNamesDrillModal();
    this.closePhotosDrillModal();
    this.closeEmailsDrillModal();
    this.closeEmailHintsDrillModal();
    this.closeNotFoundDrillModal();
    this.unknownPlatformSearchTerm = '';
    this.showUnknownDrillModal = true;
    this.syncDrillModalBodyScroll();
  }

  closeUnknownDrillModal(): void {
    this.showUnknownDrillModal = false;
    this.unknownPlatformSearchTerm = '';
    this.syncDrillModalBodyScroll();
  }

  openNotFoundDrillModal(): void {
    this.closeFoundDrillModal();
    this.closeNamesDrillModal();
    this.closePhotosDrillModal();
    this.closeEmailsDrillModal();
    this.closeEmailHintsDrillModal();
    this.closeUnknownDrillModal();
    this.notFoundPlatformSearchTerm = '';
    this.showNotFoundDrillModal = true;
    this.syncDrillModalBodyScroll();
  }

  closeNotFoundDrillModal(): void {
    this.showNotFoundDrillModal = false;
    this.notFoundPlatformSearchTerm = '';
    this.syncDrillModalBodyScroll();
  }

  onUnknownPlatformClick(platform: any): void {
    this.openDrillPlatformUrl(platform);
  }

  onNotFoundPlatformClick(platform: any): void {
    this.openDrillPlatformUrl(platform);
  }

  /** Abacus-style drill card: platform logo icon. */
  getDrillPlatformIcon(platform: any): string {
    if (platform?.icon) {
      return platform.icon;
    }
    const key = String(platform?.platformKey || platform?.platform || platform?.name || '');
    return createPlatformIconDataUrl(key) || 'assets/icons/default.png';
  }

  getDrillPlatformName(platform: any): string {
    return String(platform?.name || platform?.platform || 'Unknown').trim() || 'Unknown';
  }

  /** Prefer profile_url for the secondary line under the platform name. */
  getDrillPlatformUrlText(platform: any): string {
    const url =
      String(platform?.profile_url || '').trim() ||
      String(this.getProfileLinkDetail(platform)?.value || '').trim();
    if (!url) {
      return '';
    }
    return this.getLinkDisplayText(url);
  }

  private openDrillPlatformUrl(platform: any): void {
    const raw =
      String(platform?.profile_url || '').trim() ||
      String(this.getProfileLinkDetail(platform)?.value || '').trim();
    if (!raw) {
      return;
    }
    const url = this.utilsService.toLink(raw);
    if (!url) {
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private matchesDrillPlatformSearch(platform: any, term: string): boolean {
    const name = this.getDrillPlatformName(platform).toLowerCase();
    const url = this.getDrillPlatformUrlText(platform).toLowerCase();
    const category = String(platform?.category || '').toLowerCase();
    return name.includes(term) || url.includes(term) || category.includes(term);
  }

  onPhotoDrillImageLoad(event: Event, index: number): void {
    const img = event.target as HTMLImageElement | null;
    if (!img?.naturalWidth || !img?.naturalHeight) {
      return;
    }
    const ratio = img.naturalWidth / img.naturalHeight;
    let shape: 'portrait' | 'landscape' | 'square' = 'square';
    if (ratio < 0.85) {
      shape = 'portrait';
    } else if (ratio > 1.15) {
      shape = 'landscape';
    }
    this.photoDrillShapes = {
      ...this.photoDrillShapes,
      [index]: shape,
    };
  }

  getPhotoDrillShape(index: number): 'portrait' | 'landscape' | 'square' {
    return this.photoDrillShapes[index] || 'square';
  }

  onPhotoDrillClick(index: number): void {
    this.closePhotosDrillModal();
    setTimeout(() => this.openLightbox(index), 50);
  }

  onFoundPlatformClick(platformResult: any): void {
    const profileLink = this.getProfileLinkDetail(platformResult);
    if (profileLink && !this.isEmptyValue(profileLink.value)) {
      const url = this.utilsService.toLink(String(profileLink.value).trim());
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        this.closeFoundDrillModal();
        return;
      }
    }

    const platformKey = this.normalizePlatformNameForLookup(
      platformResult?.platform,
    );
    this.closeFoundDrillModal();
    setTimeout(() => {
      const element = document.getElementById(
        `platform-section-${platformKey}`,
      );
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        this.scrollToResultSection('source-results');
      }
    }, 50);
  }

  getFoundPlatformSubtitle(platformResult: any): string {
    const displayName = this.getFoundPlatformDisplayName(platformResult);
    const linkDetail = this.getProfileLinkDetail(platformResult);
    const linkText = linkDetail
      ? this.getLinkDisplayText(linkDetail.value)
      : '';
    const followerText = this.getFoundPlatformFollowerText(platformResult);

    if (displayName && followerText) {
      return `${displayName} - ${followerText}`;
    }
    if (displayName && linkText) {
      return `${displayName} - ${linkText}`;
    }
    if (followerText) {
      return followerText;
    }
    if (linkText) {
      return linkText;
    }
    if (displayName) {
      return displayName;
    }
    return this.getPlatformCategory(platformResult?.platform);
  }

  getFoundPlatformDisplayName(platformResult: any): string {
    const details = this.getFlattenedDetails(platformResult);
    const nameDetail = details.find((detail) => {
      const label = String(detail?.label || '')
        .toLowerCase()
        .trim();
      return (
        label === 'name' ||
        label === 'display name' ||
        label === 'full name' ||
        label === 'username' ||
        label === 'title'
      );
    });
    if (!nameDetail || this.isEmptyValue(nameDetail.value)) {
      return '';
    }
    const text = this.utilsService.formatDisplayValue(nameDetail.value);
    return text && text !== 'N/A' ? text : '';
  }

  getFoundPlatformFollowerText(platformResult: any): string {
    const details = this.getFlattenedDetails(platformResult);
    const followerDetail = details.find((detail) => {
      const label = String(detail?.label || '')
        .toLowerCase()
        .trim();
      return label.includes('follower') || label.includes('subscriber');
    });
    if (!followerDetail || this.isEmptyValue(followerDetail.value)) {
      return '';
    }
    const value = this.utilsService.formatDisplayValue(followerDetail.value);
    if (!value || value === 'N/A') {
      return '';
    }
    const label = String(followerDetail.label || '')
      .toLowerCase()
      .includes('subscriber')
      ? 'subscribers'
      : 'followers';
    return `${value} ${label}`;
  }

  getFoundPlatformThumb(platformResult: any): string {
    const image = this.getPlatformImageForCard(platformResult);
    return (
      image?.src ||
      this.resolvePlatformAvatarUrl(platformResult) ||
      platformResult?.icon ||
      ''
    );
  }

  getPlatformSectionId(platform: string): string {
    return `platform-section-${this.normalizePlatformNameForLookup(platform)}`;
  }

  getPlatformImage(platform: string): SearchImage | undefined {
    const normalizedPlatform = this.normalizePlatformNameForLookup(platform);
    const fromGallery = this.searchResultImages.find(
      (image) =>
        this.normalizePlatformNameForLookup(image.platform) ===
        normalizedPlatform,
    );
    if (fromGallery) {
      return fromGallery;
    }

    // Fallback: match platform card avatar_full / avatar (vendor results shape).
    const card = (this.searchResultDetails ?? []).find((item) => {
      const keys = [item?.platform, item?.platformKey, item?.name];
      return keys.some(
        (key) =>
          this.normalizePlatformNameForLookup(key) === normalizedPlatform,
      );
    });
    return this.buildSearchImageFromPlatformCard(card);
  }

  /** Prefer card-local avatar_full/avatar, then gallery match by platform. */
  getPlatformImageForCard(platformResult: any): SearchImage | undefined {
    // Prefer gallery entry (already face-annotated) when platform matches.
    const fromGallery = this.getPlatformImage(
      platformResult?.platform || platformResult?.platformKey || '',
    );
    if (fromGallery?.src) {
      return fromGallery;
    }
    return this.buildSearchImageFromPlatformCard(platformResult);
  }

  openPlatformImage(platform: string): void {
    const normalizedPlatform = this.normalizePlatformNameForLookup(platform);
    let index = this.searchResultImages.findIndex(
      (image) =>
        this.normalizePlatformNameForLookup(image.platform) ===
        normalizedPlatform,
    );

    // If gallery entry is missing but card has avatar_full/avatar, open that URL.
    if (index < 0) {
      const cardImage = this.getPlatformImage(platform);
      if (cardImage?.src) {
        this.selectedImage = cardImage;
        return;
      }
    }

    if (index >= 0) {
      this.openLightbox(index);
    }
  }

  getPlatformThemeStyle(platform: string): Record<string, string> {
    const accent = resolvePlatformIconVisual(platform).iconBg || '#38bdf8';
    const hex = accent.replace('#', '');
    const normalizedHex =
      hex.length === 3
        ? hex
          .split('')
          .map((character) => character + character)
          .join('')
        : hex;
    const colorValue = Number.parseInt(normalizedHex, 16);
    const isValidHex = normalizedHex.length === 6 && !Number.isNaN(colorValue);
    const red = isValidHex ? (colorValue >> 16) & 255 : 56;
    const green = isValidHex ? (colorValue >> 8) & 255 : 189;
    const blue = isValidHex ? colorValue & 255 : 248;

    return {
      '--source-accent': isValidHex ? accent : '#38bdf8',
      '--source-accent-rgb': `${red} ${green} ${blue}`,
    };
  }

  isDataDenseSourceCard(platformResult: any): boolean {
    const detailCount = (platformResult?.entries ?? []).reduce(
      (count: number, entry: any) =>
        count +
        (entry?.details ?? []).filter(
          (detail: any) => detail?.type !== 'base64String',
        ).length,
      0,
    );

    return detailCount > 6;
  }

  getPlatformCategory(platform: string): string {
    const key = String(platform || '')
      .toLowerCase()
      .trim();

    if (
      key.includes('youtube') ||
      key.includes('twitch') ||
      key.includes('vimeo') ||
      key.includes('bitchute') ||
      key.includes('trovo') ||
      key.includes('periscope') ||
      key.includes('jeuxvideo') ||
      key.includes('dailymotion')
    ) {
      return 'Video & Streaming';
    }
    if (
      key.includes('telegram') ||
      key.includes('whatsapp') ||
      key.includes('signal') ||
      key.includes('skype') ||
      key.includes('viber') ||
      key.includes('discord')
    ) {
      return 'Messaging';
    }
    if (
      key.includes('spotify') ||
      key.includes('soundcloud') ||
      key.includes('deezer') ||
      key.includes('last.fm') ||
      key.includes('lastfm') ||
      key.includes('apple music') ||
      key.includes('pandora')
    ) {
      return 'Music';
    }
    if (
      key.includes('reddit') ||
      key.includes('quora') ||
      key.includes('stackoverflow') ||
      key.includes('stack overflow') ||
      key.includes('forum') ||
      key.includes('discourse')
    ) {
      return 'Forums & Community';
    }
    if (
      key.includes('linkedin') ||
      key.includes('github') ||
      key.includes('gitlab') ||
      key.includes('bitbucket') ||
      key.includes('indeed') ||
      key.includes('glassdoor') ||
      key.includes('behance') ||
      key.includes('dribbble')
    ) {
      return 'Professional';
    }
    if (
      key.includes('facebook') ||
      key.includes('instagram') ||
      key.includes('twitter') ||
      key === 'x' ||
      key.includes('snapchat') ||
      key.includes('tiktok') ||
      key.includes('pinterest') ||
      key.includes('tumblr')
    ) {
      return 'Social Media';
    }

    return 'Other';
  }

  getPlatformInitial(platform: string): string {
    const name = String(platform || '').trim();
    return name ? name.charAt(0).toUpperCase() : '?';
  }

  getFlattenedDetails(platformResult: any): any[] {
    const details: any[] = [];
    for (const entry of platformResult?.entries ?? []) {
      for (const detail of entry?.details ?? []) {
        if (
          detail?.type === 'base64String' ||
          this.searchResultProcessorService.isHiddenDisplayField(detail?.label) ||
          this.utilsService.isNonRenderableMetadataValue(detail?.value)
        ) {
          continue;
        }
        details.push(detail);
      }
    }
    return details;
  }

  getProfileLinkDetail(platformResult: any): any | null {
    // Prefer explicit vendor profile_url on the card (always present for Abacus-style cards).
    const directUrl = String(
      platformResult?.profile_url ||
      platformResult?.profile?.profile_url ||
      platformResult?.profile?.url ||
      '',
    ).trim();
    if (directUrl) {
      return {
        label: 'Profile Url',
        value: directUrl,
        type: 'link',
      };
    }

    const details = this.getFlattenedDetails(platformResult);
    const preferred = details.find((detail) => {
      const label = String(detail?.label || '')
        .toLowerCase()
        .trim();
      return (
        detail?.type === 'link' &&
        (label.includes('profile') || label.includes('url') || label === 'link')
      );
    });
    if (preferred) {
      return preferred;
    }
    return details.find((detail) => detail?.type === 'link') ?? null;
  }

  getProfileLinkTitle(platformResult: any): string {
    const platform = String(
      platformResult?.platform || platformResult?.name || 'profile',
    ).trim();
    return `Open ${platform} profile in a new tab`;
  }

  getCardDescription(platformResult: any): string | null {
    const details = this.getFlattenedDetails(platformResult);
    const descriptionDetail = details.find((detail) => {
      const label = String(detail?.label || '')
        .toLowerCase()
        .trim();
      return label === 'description' || label === 'bio' || label === 'about';
    });
    if (!descriptionDetail || this.isEmptyValue(descriptionDetail.value)) {
      return null;
    }
    const text = this.utilsService.formatDisplayValue(descriptionDetail.value);
    return text && text !== 'N/A' ? text : null;
  }

  getCardBodyDetails(platformResult: any): any[] {
    const profileLink = this.getProfileLinkDetail(platformResult);
    const profileUrlKey = this.comparableProfileUrl(profileLink?.value);
    const description = this.getCardDescription(platformResult);

    return this.getFlattenedDetails(platformResult).filter((detail) => {
      if (profileLink && detail === profileLink) {
        return false;
      }
      // profile_url is already the link under the card; drop the same URL field.
      if (this.detailDuplicatesProfileLink(detail, profileUrlKey)) {
        return false;
      }
      const label = String(detail?.label || '')
        .toLowerCase()
        .trim();
      if (
        label === 'description' ||
        label === 'bio' ||
        label === 'about' ||
        label === 'category' ||
        label === 'category name' ||
        label === 'category type' ||
        label.includes('picture') ||
        label.includes('photo') ||
        label.includes('avatar') ||
        label.includes('image')
      ) {
        return false;
      }
      if (description && detail?.type !== 'link') {
        const formatted = this.utilsService.formatDisplayValue(detail?.value);
        if (formatted === description) {
          return false;
        }
      }
      if (this.isBlankPlaceholder(detail?.value)) {
        return false;
      }
      return true;
    });
  }

  private comparableProfileUrl(value: any): string {
    const raw = String(value ?? '').trim();
    if (!raw) {
      return '';
    }
    return this.getLinkDisplayText(raw).toLowerCase();
  }

  private detailDuplicatesProfileLink(detail: any, profileUrlKey: string): boolean {
    if (!profileUrlKey) {
      return false;
    }
    const label = String(detail?.label || '').toLowerCase();
    const raw = String(detail?.value ?? '').trim();
    const looksLikeUrl =
      detail?.type === 'link' ||
      /^https?:\/\//i.test(raw) ||
      label.includes('url') ||
      label.includes('link') ||
      label.includes('profile');
    if (!looksLikeUrl || !raw) {
      return false;
    }
    return this.comparableProfileUrl(raw) === profileUrlKey;
  }

  getLinkDisplayText(value: any): string {
    const raw = String(value ?? '').trim();
    if (!raw) {
      return '';
    }
    try {
      const url = new URL(this.utilsService.toLink(raw));
      const path = `${url.host}${url.pathname}`.replace(/\/$/, '');
      return path || raw;
    } catch {
      return raw.replace(/^https?:\/\//i, '').replace(/\/$/, '');
    }
  }

  isActiveStatusValue(detail: any): boolean {
    const label = String(detail?.label || '')
      .toLowerCase()
      .trim();
    if (!label.includes('status')) {
      return false;
    }
    const value = String(detail?.value ?? '')
      .toLowerCase()
      .trim();
    return value === 'active' || value === 'yes' || value === 'true';
  }

  getAvatarInitial(): string {
    const username = this.selectedSearchResult?.searched_username;
    if (!username) {
      return 'U';
    }
    return String(username).charAt(0).toUpperCase();
  }

  toggleSummaryCollapse(
    section: 'names' | 'emails' | 'emailHints' | 'photos',
  ): void {
    if (section === 'names' || section === 'emails') {
      const next = !this.summaryCollapsed[section];
      this.summaryCollapsed.names = next;
      this.summaryCollapsed.emails = next;
      return;
    }
    this.summaryCollapsed[section] = !this.summaryCollapsed[section];
  }

  copySummaryValue(value: string): void {
    const text = String(value ?? '').trim();
    if (!text) {
      return;
    }

    const onSuccess = () => {
      this.snackBar.open('Copied to clipboard', 'Close', {
        duration: 2000,
        panelClass: ['snackbar-success'],
      });
    };
    const onError = () => {
      this.snackBar.open('Failed to copy', 'Close', {
        duration: 2000,
        panelClass: ['snackbar-error'],
      });
    };

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(onSuccess).catch(onError);
      return;
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      onSuccess();
    } catch {
      onError();
    }
  }

  getSectionIconClass(platform: string): string {
    const key = String(platform || '').toLowerCase().trim();

    if (key.includes('truecaller')) return 'fas fa-phone';
    if (key.includes('whatsapp')) return 'fab fa-whatsapp';
    if (key.includes('telegram')) return 'fab fa-telegram-plane';
    if (key.includes('instagram')) return 'fab fa-instagram';
    if (key.includes('facebook')) return 'fab fa-facebook-f';
    if (key.includes('linkedin')) return 'fab fa-linkedin-in';
    if (key.includes('github')) return 'fab fa-github';
    if (key.includes('twitter') || key === 'x') return 'fab fa-x-twitter';
    if (key.includes('snapchat')) return 'fab fa-snapchat-ghost';
    if (key.includes('google pay') || key.includes('gpay')) return 'fas fa-wallet';
    if (key.includes('phonepe')) return 'fas fa-wallet';
    if (key.includes('paytm')) return 'fas fa-wallet';
    if (key.includes('microsoft')) return 'fab fa-microsoft';
    if (key.includes('mail') || key.includes('email')) return 'fas fa-envelope';

    return 'fas fa-circle-notch';
  }

  getDetailIconClass(detail: any): string {
    const label = String(detail?.label || '').toLowerCase();
    const type = String(detail?.type || '').toLowerCase();

    if (label.includes('email')) return 'far fa-envelope';
    if (
      label.includes('location') ||
      label.includes('city') ||
      label.includes('country')
    ) {
      return 'fas fa-map-marker-alt';
    }
    if (
      label.includes('phone') ||
      label.includes('mobile') ||
      label.includes('number')
    ) {
      return 'fas fa-phone';
    }
    if (type === 'link' || label.includes('url') || label.includes('link')) {
      return 'fas fa-link';
    }
    if (
      label.includes('name') ||
      label.includes('username') ||
      label.includes('profile')
    ) {
      return 'far fa-user';
    }
    if (
      type === 'date' ||
      type === 'datetime' ||
      label.includes('date') ||
      label.includes('time')
    ) {
      return 'far fa-calendar-alt';
    }
    if (
      label.includes('carrier') ||
      label.includes('network') ||
      label.includes('operator')
    ) {
      return 'fas fa-signal';
    }

    return 'fas fa-info-circle';
  }

  scrollToSection(sectionId: string, event?: Event): void {
    event?.preventDefault();
    const target = document.getElementById(sectionId);
    if (!target) {
      return;
    }
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private searchPollingService: SearchPollingService,
    private osintSearchService: OsintSearchService,
    private searchServiceStatusService: SearchServiceStatusService,
    private searchResultProcessorService: SearchResultProcessorService,
    public utilsService: UtilsService,
    private gallery: Gallery,
    private lightbox: Lightbox,
    private ngZone: NgZone,
    private googleOsintQueryBuilder: GoogleOsintQueryBuilder,
    private googleCseService: GoogleCseService,
  ) { }

  /**
   * Left-to-right masonry: place cards in sequence into the shortest column
   * so tall cards do not leave empty gaps beside them.
   */
  scheduleMasonryLayout(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (this.masonryRafId != null) {
      cancelAnimationFrame(this.masonryRafId);
    }
    this.masonryRafId = requestAnimationFrame(() => {
      this.masonryRafId = requestAnimationFrame(() => {
        this.masonryRafId = null;
        this.layoutMasonryGrid();
      });
    });
  }

  private getMasonryColumnCount(gridWidth: number, cardCount: number): number {
    if (cardCount <= 1) return 1;
    if (gridWidth <= 768) return 1;
    if (cardCount === 2 || gridWidth <= 1100) return 2;
    return 3;
  }

  private layoutMasonryGrid(): void {
    const grid = document.getElementById('source-results');
    if (!grid) {
      return;
    }

    const cards = Array.from(
      grid.querySelectorAll(':scope > .source-card'),
    ) as HTMLElement[];

    if (!cards.length) {
      grid.style.height = '';
      grid.classList.remove('is-masonry');
      return;
    }

    const gap = this.masonryGapPx;
    const width = grid.clientWidth;
    if (width <= 0) {
      return;
    }

    const cols = this.getMasonryColumnCount(width, cards.length);
    const usableWidth =
      cards.length === 1 ? Math.min(width, 22 * 16) : width;
    const colWidth = (usableWidth - gap * (cols - 1)) / cols;
    const colHeights = new Array(cols).fill(0);

    grid.classList.add('is-masonry');
    grid.style.maxWidth = cards.length === 1 ? '22rem' : '';

    // Set widths first so measured heights are accurate.
    for (const card of cards) {
      card.style.position = 'absolute';
      card.style.boxSizing = 'border-box';
      card.style.width = `${colWidth}px`;
      card.style.margin = '0';
      card.style.left = '0';
      card.style.top = '0';
    }

    // Place each card (in DOM / list order) into the current shortest column.
    for (const card of cards) {
      let shortest = 0;
      for (let i = 1; i < cols; i++) {
        if (colHeights[i] < colHeights[shortest]) {
          shortest = i;
        }
      }
      const left = shortest * (colWidth + gap);
      const top = colHeights[shortest];
      card.style.left = `${left}px`;
      card.style.top = `${top}px`;
      colHeights[shortest] = top + card.offsetHeight + gap;
    }

    const tallest = Math.max(...colHeights);
    grid.style.height = `${Math.max(0, tallest - gap)}px`;
    this.lastMasonryWidth = Math.round(width);

    // Relayout when card images finish loading (height changes).
    grid.querySelectorAll('img').forEach((img) => {
      const image = img as HTMLImageElement;
      if (image.dataset['masonryBound'] === '1') {
        return;
      }
      image.dataset['masonryBound'] = '1';
      if (!image.complete) {
        image.addEventListener('load', () => this.scheduleMasonryLayout(), {
          once: true,
        });
        image.addEventListener('error', () => this.scheduleMasonryLayout(), {
          once: true,
        });
      }
    });

    this.observeMasonryGridWidth(grid);
  }

  private lastMasonryWidth = 0;

  private observeMasonryGridWidth(grid: HTMLElement): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    if (!this.masonryResizeObserver) {
      this.ngZone.runOutsideAngular(() => {
        this.masonryResizeObserver = new ResizeObserver((entries) => {
          const nextWidth = Math.round(entries[0]?.contentRect?.width ?? 0);
          // Ignore height-only changes from our own absolute positioning.
          if (nextWidth === this.lastMasonryWidth) {
            return;
          }
          this.scheduleMasonryLayout();
        });
      });
    }
    this.masonryResizeObserver?.observe(grid);
  }

  @HostListener('window:resize')
  onWindowResizeForMasonry(): void {
    this.scheduleMasonryLayout();
  }

  loadGalleryImages(images: any) {
    this.items = [];
    images.forEach((image: any) => {
      this.items.push(
        new ImageItem({
          src: image.src,
          thumb: image.src,
        }),
      );
    });
    this.gallery
      .ref('myGallery', {
        thumbPosition: 'bottom', // 👈 thumbnails at bottom
        imageSize: 'contain',
        loop: false,
        thumbWidth: 120,
        thumbHeight: 80,
      })
      .load(this.items);
  }

  openLightbox(index: number = 0) {
    this.lightbox.open(index, 'myGallery', {
      panelClass: 'fullscreen-lightbox',
      hasBackdrop: true,
      keyboardShortcuts: true,
      closeIcon: '✕',
    });
  }

  ngOnInit() {
    this.setupFilterScrollListener();
    this.selectedSearchResult = history.state?.searchResult;
    this.searchId = this.selectedSearchResult?.osint_search_id;

    if (!this.selectedSearchResult) {
      this.router.navigate(['/osint-username-search']);
      return;
    }

    // History row job_id is our portal UUID used for /osint/job polling.
    if (
      this.selectedSearchResult.job_id &&
      !this.selectedSearchResult.portal_job_id
    ) {
      this.selectedSearchResult.portal_job_id = this.selectedSearchResult.job_id;
    }

    if (this.selectedSearchResult.done) {
      this.getCompletedSearchResult();
    } else {
      this.pollForSearchResult();
    }
  }

  ngAfterViewInit(): void {
    this.scheduleMasonryLayout();
  }

  ngOnDestroy() {
    this.teardownFilterScrollListener();
    if (this.masonryRafId != null) {
      cancelAnimationFrame(this.masonryRafId);
      this.masonryRafId = null;
    }
    this.masonryResizeObserver?.disconnect();
    this.masonryResizeObserver = null;
    if (this.selectedImage || this.showFoundDrillModal || this.showNamesDrillModal || this.showPhotosDrillModal || this.showEmailsDrillModal || this.showEmailHintsDrillModal || this.showUnknownDrillModal || this.showNotFoundDrillModal) {
      document.body.style.overflow = '';
    }
    this.jobResultSubscription?.unsubscribe();
    this.vendorSyncSubscription?.unsubscribe();
    this.faceDetectionRefreshSubscription?.unsubscribe();
    this.searchResultProcessorService.cleanupBlobUrls();
  }

  onBackButtonClick() {
    this.router.navigate(['/osint-username-search']);
  }

  setViewTab(tab: 'results' | 'ai-analysis'): void {
    this.activeViewTab = tab;
  }

  onIconError(event: Event) {
    const imgElement = event.target as HTMLImageElement | null;
    if (!imgElement) {
      return;
    }

    // If we've already processed this element, just hide it and return
    if (this.processedIcons.has(imgElement)) {
      imgElement.style.display = 'none';
      return;
    }

    const defaultIcon = 'assets/icons/default.png';
    const currentSrc = imgElement.src || '';

    // Check if we're already trying to load the default icon
    if (currentSrc.includes('default.png')) {
      // Already loading default, mark as processed and hide
      this.processedIcons.add(imgElement);
      imgElement.style.display = 'none';
      return;
    }

    // Mark this element as processed BEFORE changing src
    this.processedIcons.add(imgElement);

    // Set default icon
    imgElement.src = defaultIcon;

    // If default icon also fails, the error event will fire again
    // but processedIcons check will catch it and just hide the image
  }

  onPictureError(event: Event): void {
    const img = event.target as HTMLImageElement | null;
    if (!img) {
      return;
    }
    if (img.dataset['avatarFallback'] === '1') {
      img.style.display = 'none';
      return;
    }
    img.dataset['avatarFallback'] = '1';
    img.classList.add('is-fallback');
    img.closest('.abacus-picture-frame')?.classList.add('is-fallback');
    img.closest('.found-drill-thumb-wrap')?.classList.add('is-fallback');
    img.src = DEFAULT_PROFILE_AVATAR_SRC;
  }

  canStartImageSearch(image?: ImageSearchCandidate): boolean {
    return canStartImageSearchUtil(image);
  }

  startImageSearchFromSrc(
    imageOrSrc: ImageSearchCandidate | string,
    event?: Event,
  ): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.canStartImageSearch(imageOrSrc)) {
      return;
    }

    const src = resolveImageSearchSrc(imageOrSrc);

    if (!src) {
      return;
    }

    void navigateToOsintImageSearch(this.router, src).catch(() => {
      this.snackBar.open('Unable to start Image Search', 'Close', {
        duration: 3000,
      });
    });
  }

  getCompletedSearchResult(options?: {
    progressive?: boolean;
    silent?: boolean;
    faceRefreshOnly?: boolean;
  }) {
    const progressive = Boolean(options?.progressive);
    const silent = Boolean(options?.silent);
    const faceRefreshOnly = Boolean(options?.faceRefreshOnly);
    const payload = {
      type: 'username',
      searchId: this.searchId,
    };
    // Progressive mid-poll refreshes should not flash a full-page loader once
    // we already have some results on screen. Silent face refreshes never flash.
    if (!silent && (!progressive || this.isLoadingResults)) {
      this.loadingCompletedSearchResult = true;
    }
    const originalSearchedUsername =
      this.selectedSearchResult?.searched_username;

    this.osintSearchService
      .getCompletedSearchResult(payload.type, payload.searchId)
      .subscribe({
        next: (response) => {
          // Username results API now returns the same shape as the 3rd-party API:
          // data = { job_id, username, status, done, progress, stats, names, avatars, emails, results, cursor, ... }
          const data = response?.data || {};
          const hasVendorShape =
            Array.isArray(data?.results) ||
            data?.username != null ||
            data?.stats != null;

          if (faceRefreshOnly) {
            this.applyFaceDetectionRefreshFromPayload(data);
            return;
          }

          if (!hasVendorShape && Array.isArray(data?.data_list)) {
            // Legacy fallback (older backend responses)
            this.applyLegacyCompletedSearchResult(
              data,
              originalSearchedUsername,
              { progressive },
            );
            this.maybeStartFaceDetectionRefresh();
            return;
          }

          const refreshPending = Boolean(data?.background_refresh_pending);
          const historyDone = Boolean(
            data?.done ?? this.selectedSearchResult?.done,
          );
          const progress = data?.progress || null;
          const resolvedPercent = this.resolveProgressNumber(
            progress?.percent,
            this.selectedSearchResult?.progress_percent,
            this.selectedSearchResult?.progress_milestone,
          );
          const portalJobId =
            data?.portal_job_id ||
            this.selectedSearchResult?.portal_job_id ||
            this.selectedSearchResult?.job_id ||
            null;
          const vendorSyncComplete =
            !progressive && historyDone && !refreshPending;

          this.selectedSearchResult = {
            ...this.selectedSearchResult,
            ...data,
            searched_username:
              data?.username ||
              data?.query ||
              originalSearchedUsername ||
              this.selectedSearchResult?.searched_username,
            osint_search_id:
              data?.osint_search_id ||
              this.searchId ||
              this.selectedSearchResult?.osint_search_id,
            result_id:
              data?.result_id || this.selectedSearchResult?.result_id || null,
            // Keep 3rd-party job_id on `job_id`; portal UUID used for /osint/job polling.
            job_id: data?.job_id || this.selectedSearchResult?.job_id || null,
            portal_job_id: portalJobId,
            done: progressive
              ? Boolean(this.selectedSearchResult?.done) || historyDone
              : historyDone,
            background_refresh_pending: progressive
              ? true
              : refreshPending,
            vendor_sync_complete: vendorSyncComplete,
            is_final: vendorSyncComplete,
            progress_percent:
              resolvedPercent ?? this.selectedSearchResult?.progress_percent,
            progress_milestone:
              progress?.percent ??
              this.selectedSearchResult?.progress_milestone,
            progress,
            stats: data?.stats || null,
            results: Array.isArray(data?.results) ? data.results : [],
            names: Array.isArray(data?.names) ? data.names : [],
            avatars: Array.isArray(data?.avatars) ? data.avatars : [],
            emails: Array.isArray(data?.emails) ? data.emails : [],
            name: Array.isArray(data?.names) ? data.names : [],
            email: this.normalizeVendorEmails(data?.emails),
            faceDetected:
              data?.faceDetected && typeof data.faceDetected === 'object'
                ? data.faceDetected
                : this.selectedSearchResult?.faceDetected || null,
            face_detection_pending: Boolean(data?.face_detection_pending),
            face_detection_ready: Boolean(data?.face_detection_ready),
            status: vendorSyncComplete
              ? 'completed'
              : data?.status || this.selectedSearchResult?.status || 'pending',
          };
          this.applyPlatformCategoriesFromResponse(data);
          this.notes = data?.notes || '';
          this.searchId =
            this.selectedSearchResult?.osint_search_id || this.searchId;

          if (
            (Array.isArray(data?.results) && data.results.length > 0) ||
            (Array.isArray(data?.names) && data.names.length > 0) ||
            (Array.isArray(data?.avatars) && data.avatars.length > 0) ||
            (Array.isArray(data?.emails) && data.emails.length > 0)
          ) {
            this.noRecords = false;
            this.processSearchResult(data);
            setTimeout(() => this.initGoogleResults(), 100);
          } else if (!progressive) {
            this.noRecords = true;
            this.searchResultDetails = [];
            this.searchResultDetailsFiltered = [];
            this.searchResultImages = [];
          }

          this.loadingCompletedSearchResult = false;
          if (!progressive || vendorSyncComplete) {
            this.loadingPolledSearchResult = false;
          } else if (
            this.searchResultDetails?.length ||
            this.searchResultImages?.length
          ) {
            // Partial data is on screen — stop the empty-state spinner.
            this.loadingPolledSearchResult = false;
          }

          if (progressive) {
            // Keep existing poll subscriptions alive while vendor is still running.
            return;
          }

          if (refreshPending) {
            if (!this.selectedSearchResult?.vendor_sync_complete) {
              this.jobResultSubscription?.unsubscribe();
              this.watchVendorSyncViaJobPollThenReload();
            } else {
              this.jobResultSubscription?.unsubscribe();
              this.vendorSyncSubscription?.unsubscribe();
            }
          } else {
            this.selectedSearchResult = {
              ...this.selectedSearchResult,
              vendor_sync_complete: true,
              is_final: true,
              background_refresh_pending: false,
              status: 'completed',
            };
            this.jobResultSubscription?.unsubscribe();
            this.vendorSyncSubscription?.unsubscribe();
            this.maybeStartFaceDetectionRefresh();
          }
        },
        error: (error) => {
          console.error(error);
          if (!silent) {
            this.loadingCompletedSearchResult = false;
            if (!progressive) {
              this.loadingPolledSearchResult = false;
            }
          }
        },
      });
  }

  /** Older /osint/results username shape with data_list[]. */
  private applyLegacyCompletedSearchResult(
    rawSearchResultData: any,
    originalSearchedUsername: string,
    options?: { progressive?: boolean },
  ) {
    const progressive = Boolean(options?.progressive);
    const completedSearchResult = rawSearchResultData?.data_list?.[0];
    const refreshPending = Boolean(
      rawSearchResultData?.background_refresh_pending,
    );
    const historyDone = Boolean(
      rawSearchResultData?.done ?? this.selectedSearchResult?.done,
    );
    const progressFromResult =
      completedSearchResult?.extra_data?.progress ||
      rawSearchResultData?.progress ||
      null;
    const resolvedPercent = this.resolveProgressNumber(
      rawSearchResultData?.progress_percent,
      this.selectedSearchResult?.progress_percent,
      progressFromResult?.percent,
      progressFromResult?.milestone,
    );
    const vendorSyncComplete =
      !progressive && historyDone && !refreshPending;

    this.selectedSearchResult = {
      ...this.selectedSearchResult,
      ...completedSearchResult,
      searched_username:
        completedSearchResult?.searched_username || originalSearchedUsername,
      done: progressive
        ? Boolean(this.selectedSearchResult?.done) || historyDone
        : historyDone,
      background_refresh_pending: progressive ? true : refreshPending,
      vendor_sync_complete: vendorSyncComplete,
      is_final: vendorSyncComplete,
      progress_percent:
        resolvedPercent ?? this.selectedSearchResult?.progress_percent,
      progress: progressFromResult || this.selectedSearchResult?.progress,
      vendor:
        rawSearchResultData?.vendor ||
        completedSearchResult?.extra_data?.vendor ||
        null,
      stats:
        rawSearchResultData?.stats ||
        completedSearchResult?.extra_data?.vendor?.stats ||
        completedSearchResult?.extra_data?.stats ||
        null,
      results:
        rawSearchResultData?.results ||
        completedSearchResult?.extra_data?.vendor?.results ||
        null,
    };
    this.applyPlatformCategoriesFromResponse({
      categories: rawSearchResultData?.categories,
      results:
        this.selectedSearchResult?.results ||
        completedSearchResult?.extra_data?.vendor?.results ||
        [],
    });
    this.notes = rawSearchResultData?.notes || '';

    if (rawSearchResultData?.data_list?.length) {
      this.noRecords = false;
      this.processSearchResult(rawSearchResultData);
      setTimeout(() => this.initGoogleResults(), 100);
    } else if (!progressive) {
      this.noRecords = true;
    }

    this.loadingCompletedSearchResult = false;
    if (!progressive || vendorSyncComplete) {
      this.loadingPolledSearchResult = false;
    } else if (
      this.searchResultDetails?.length ||
      this.searchResultImages?.length
    ) {
      this.loadingPolledSearchResult = false;
    }

    if (progressive) {
      return;
    }

    if (refreshPending && !this.selectedSearchResult?.vendor_sync_complete) {
      this.jobResultSubscription?.unsubscribe();
      this.watchVendorSyncViaJobPollThenReload();
    } else {
      this.jobResultSubscription?.unsubscribe();
      this.vendorSyncSubscription?.unsubscribe();
    }
  }

  private watchVendorSyncViaJobPollThenReload() {
    const jobId =
      this.selectedSearchResult?.portal_job_id ||
      this.selectedSearchResult?.job_id;
    if (!jobId) {
      return;
    }
    this.vendorSyncSubscription?.unsubscribe();
    this.ensureUsernameJobPolling(jobId);

    let lastProgressBucket: number | null = this.toProgressBucket(
      this.resolveProgressNumber(
        this.selectedSearchResult?.progress?.percent,
        this.selectedSearchResult?.progress_percent,
        this.selectedSearchResult?.progress_milestone,
      ),
    );
    let lastEmailsReady = Boolean(this.selectedSearchResult?.emails_ready);

    const handleStatus = (s: any) => {
      if (!s) {
        return;
      }

      this.applyPollStatusToSelectedResult(s);

      const percent = toPercent(
        s?.progress?.percent,
        s.progress_milestone,
        s.progress_percent,
      );
      const bucket = this.toProgressBucket(percent);
      const emailsReady = Boolean(s.emails_ready);

      if (isOsintJobVendorSyncComplete(s)) {
        this.markResultPollComplete(s);
        if (
          s.deleted === true ||
          s.status === 'deleted' ||
          s.timed_out === true ||
          s.status === 'error'
        ) {
          return;
        }
        this.getCompletedSearchResult();
        return;
      }

      const bucketIncreased =
        bucket != null &&
        bucket >= 5 &&
        (lastProgressBucket == null || bucket > lastProgressBucket);
      const emailsJustReady = emailsReady && !lastEmailsReady;

      if (bucketIncreased || emailsJustReady) {
        if (bucketIncreased) {
          lastProgressBucket = bucket;
        }
        if (emailsJustReady) {
          lastEmailsReady = true;
        }
        this.getCompletedSearchResult({ progressive: true });
      }
    };

    // Apply any cached status immediately (do not wait for the next poll tick)
    const cached = this.searchPollingService.getCachedJobStatus(jobId);
    if (cached) {
      handleStatus(cached);
      if (isOsintJobVendorSyncComplete(cached)) {
        return;
      }
    }

    this.vendorSyncSubscription = this.searchPollingService
      .getJobResult(jobId)
      .subscribe(handleStatus);
  }

  pollForSearchResult() {
    const jobId =
      this.selectedSearchResult?.portal_job_id ||
      this.selectedSearchResult?.job_id;

    this.jobResultSubscription?.unsubscribe();
    this.loadingPolledSearchResult = true;
    this.ensureUsernameJobPolling(jobId);

    const originalSearchedUsername =
      this.selectedSearchResult?.searched_username;
    let lastProgressBucket: number | null = null;
    let lastEmailsReady = false;

    const handleStatus = (response: any) => {
      if (!response) {
        return;
      }

      this.applyPollStatusToSelectedResult(response);
      this.selectedSearchResult = {
        ...this.selectedSearchResult,
        searched_username:
          response?.searched_username || originalSearchedUsername,
      };

      const percent = toPercent(
        response?.progress?.percent,
        response.progress_milestone,
        response.progress_percent,
      );
      const bucket = this.toProgressBucket(percent);
      const emailsReady = Boolean(response.emails_ready);

      if (isOsintJobVendorSyncComplete(response)) {
        this.markResultPollComplete(response);
        if (
          response.deleted === true ||
          response.status === 'deleted' ||
          response.timed_out === true ||
          response.status === 'error'
        ) {
          return;
        }
        this.getCompletedSearchResult();
        return;
      }

      const bucketIncreased =
        bucket != null &&
        bucket >= 5 &&
        (lastProgressBucket == null || bucket > lastProgressBucket);
      const emailsJustReady = emailsReady && !lastEmailsReady;

      if (bucketIncreased || emailsJustReady) {
        if (bucketIncreased) {
          lastProgressBucket = bucket;
        }
        if (emailsJustReady) {
          lastEmailsReady = true;
        }
        this.getCompletedSearchResult({ progressive: true });
      }
    };

    const cached = this.searchPollingService.getCachedJobStatus(jobId);
    if (cached) {
      handleStatus(cached);
      if (isOsintJobVendorSyncComplete(cached)) {
        this.loadingPolledSearchResult = false;
        return;
      }
    }

    this.jobResultSubscription = this.searchPollingService
      .getJobResult(jobId)
      .subscribe({
        next: handleStatus,
        error: (error) => {
          console.error(error);
          this.loadingPolledSearchResult = false;
        },
      });
  }

  private extractImagesFromSources(apiResponseData: any) {
    const vendor = this.resolveVendorPayload(apiResponseData);
    if (vendor) {
      this.applyFaceDetectionToImages(this.buildImagesFromVendorPayload(vendor));
      return;
    }

    this.loadingImages = true;
    this.searchResultProcessorService
      .extractImagesFromSources(apiResponseData)
      .subscribe({
        next: (images) => {
          this.ngZone.run(() => {
            this.searchResultImages = images;
            this.loadGalleryImages(images);
            this.loadingImages = false;
            this.scheduleMasonryLayout();
          });
        },
        error: (error) => {
          console.error('Error extracting images:', error);
          this.ngZone.run(() => {
            this.searchResultImages = [];
            this.loadingImages = false;
          });
        },
      });
  }

  private applyFaceDetectionToImages(images: SearchImage[]): void {
    this.searchResultImages = images;
    this.loadGalleryImages(images);
    this.loadingImages = false;
    this.scheduleMasonryLayout();

    const faceDetectedMap =
      this.searchResultProcessorService.resolveFaceDetectedMap(
        this.selectedSearchResult,
      ) ||
      this.searchResultProcessorService.resolveFaceDetectedMap({
        faceDetected: this.selectedSearchResult?.faceDetected,
        vendor: this.selectedSearchResult,
      });

    const faceDetectionPending =
      isFaceDetectionPending(this.selectedSearchResult) ||
      this.selectedSearchResult?.face_detection_ready === false;

    this.searchResultProcessorService
      .annotateImagesForFaceSearch(images, faceDetectedMap, {
        faceDetectionPending: Boolean(
          faceDetectionPending &&
            !(
              faceDetectedMap &&
              Object.keys(faceDetectedMap).length > 0
            ),
        ),
      })
      .subscribe({
        next: (annotated) => {
          this.ngZone.run(() => {
            this.searchResultImages = annotated;
            this.loadGalleryImages(annotated);
            this.scheduleMasonryLayout();
          });
        },
        error: () => {
          this.ngZone.run(() => {
            this.searchResultImages = images.map((image) => ({
              ...image,
              faceDetected: false,
            }));
            this.loadGalleryImages(this.searchResultImages);
            this.scheduleMasonryLayout();
          });
        },
      });
  }

  /**
   * Soft-poll /osint/results until face_detection_ready so icons appear without
   * a manual page reload. History already has ready=true / a map → skipped.
   */
  private maybeStartFaceDetectionRefresh(): void {
    if (!hasFaceSearchPermission()) {
      return;
    }
    if (!this.searchId) {
      return;
    }
    if (!shouldSoftPollFaceDetection(this.selectedSearchResult)) {
      return;
    }
    if (this.faceDetectionRefreshSubscription) {
      return;
    }

    const maxAttempts = 24; // ~60s at 2.5s
    let attempt = 0;

    this.faceDetectionRefreshSubscription = timer(1500, 2500)
      .pipe(
        takeWhile(() => attempt < maxAttempts, true),
        switchMap(() => {
          attempt += 1;
          return this.osintSearchService.getCompletedSearchResult(
            'username',
            this.searchId,
          );
        }),
      )
      .subscribe({
        next: (response) => {
          const data = response?.data || {};
          const ready = this.applyFaceDetectionRefreshFromPayload(data);
          if (ready || attempt >= maxAttempts) {
            this.faceDetectionRefreshSubscription?.unsubscribe();
            this.faceDetectionRefreshSubscription = undefined;
          }
        },
        error: () => {
          this.faceDetectionRefreshSubscription?.unsubscribe();
          this.faceDetectionRefreshSubscription = undefined;
        },
      });
  }

  /** Apply newly persisted face flags in-place (no full result rebuild / spinner). */
  private applyFaceDetectionRefreshFromPayload(data: any): boolean {
    if (!data || typeof data !== 'object') {
      return false;
    }

    const faceDetected =
      data.faceDetected && typeof data.faceDetected === 'object'
        ? data.faceDetected
        : null;
    const ready =
      isFaceDetectionReady(data) ||
      Boolean(faceDetected && Object.keys(faceDetected).length > 0);

    this.selectedSearchResult = {
      ...this.selectedSearchResult,
      faceDetected:
        faceDetected || this.selectedSearchResult?.faceDetected || null,
      face_detection_pending: Boolean(data.face_detection_pending),
      face_detection_ready: Boolean(data.face_detection_ready || ready),
      results: Array.isArray(data.results)
        ? data.results
        : this.selectedSearchResult?.results,
    };

    if (!ready) {
      return false;
    }

    // Re-stamp platform cards from results[].faceDetected / map.
    if (Array.isArray(data.results) && data.results.length) {
      const vendor = this.resolveVendorPayload({
        ...data,
        faceDetected: faceDetected || data.faceDetected,
      });
      if (vendor) {
        this.searchResultDetails = (vendor.results || []).map((item: any) =>
          this.mapVendorResultToPlatformCard(item),
        );
        this.searchResultDetailsFiltered = JSON.parse(
          JSON.stringify(this.searchResultDetails),
        );
        const images = this.buildImagesFromVendorPayload(vendor);
        this.applyFaceDetectionToImages(images);
      }
    } else if (this.searchResultImages?.length) {
      this.applyFaceDetectionToImages([...this.searchResultImages]);
    }

    return true;
  }

  /** Prefer the 3rd-party username payload shape. */
  private resolveVendorPayload(searchResult: any): any | null {
    if (!searchResult || typeof searchResult !== 'object') {
      return null;
    }

    // New API: data itself is the 3rd-party shape
    if (Array.isArray(searchResult.results)) {
      return {
        job_id: searchResult.job_id ?? null,
        username:
          searchResult.username ||
          searchResult.query ||
          this.selectedSearchResult?.searched_username ||
          null,
        status: searchResult.status ?? null,
        done: Boolean(searchResult.done),
        progress: searchResult.progress ?? null,
        stats: searchResult.stats ?? null,
        names: Array.isArray(searchResult.names) ? searchResult.names : [],
        avatars: Array.isArray(searchResult.avatars) ? searchResult.avatars : [],
        emails: Array.isArray(searchResult.emails) ? searchResult.emails : [],
        results: searchResult.results,
        faceDetected:
          searchResult.faceDetected &&
          typeof searchResult.faceDetected === 'object'
            ? searchResult.faceDetected
            : null,
        ...(searchResult.cursor !== undefined
          ? { cursor: searchResult.cursor }
          : {}),
      };
    }

    const nested = [
      searchResult?.vendor,
      searchResult?.data_list?.[0]?.extra_data?.vendor,
      this.selectedSearchResult?.vendor,
      this.selectedSearchResult?.extra_data?.vendor,
    ];
    for (const candidate of nested) {
      if (
        candidate &&
        typeof candidate === 'object' &&
        Array.isArray(candidate.results)
      ) {
        return candidate;
      }
    }
    return null;
  }

  processSearchResult(searchResult: any) {
    this.noRecords = false;
    const vendor = this.resolveVendorPayload(searchResult);

    if (vendor) {
      this.applyVendorSearchResult(vendor, searchResult);
      return;
    }

    // Legacy sources-based rendering
    if (!searchResult?.data_list?.[0]) {
      this.noRecords = true;
      this.scheduleMasonryLayout();
      return;
    }

    this.extractImagesFromSources(searchResult);
    const sourceKeysWithNoAccount = this.getSourceKeysWithNoAccount(searchResult);
    const processedResult = this.searchResultProcessorService.process(
      searchResult.data_list[0],
    );

    this.searchResultDetails = (processedResult.platforms ?? []).map(
      (platform: any) => ({
        ...platform,
        notFound: sourceKeysWithNoAccount.has(
          this.normalizePlatformNameForLookup(platform?.platformKey || platform?.platform),
        ),
      }),
    );
    this.searchResultDetailsFiltered = JSON.parse(
      JSON.stringify(this.searchResultDetails),
    );
    this.updateAggregatedData();

    if (
      (!this.searchResultDetailsFiltered ||
        this.searchResultDetailsFiltered.length === 0) &&
      (!this.searchResultImages || this.searchResultImages.length === 0)
    ) {
      this.noRecords = true;
    }
    this.scheduleMasonryLayout();
  }

  private applyVendorSearchResult(vendor: any, _searchResult?: any) {
    this.selectedSearchResult = {
      ...this.selectedSearchResult,
      ...vendor,
      searched_username:
        vendor.username || this.selectedSearchResult?.searched_username,
      stats: vendor.stats || this.selectedSearchResult?.stats,
      results: vendor.results,
      names: vendor.names,
      avatars: vendor.avatars,
      emails: vendor.emails,
      name: Array.isArray(vendor.names) ? vendor.names : [],
      email: this.normalizeVendorEmails(vendor.emails),
      progress: vendor.progress || this.selectedSearchResult?.progress,
      faceDetected:
        vendor.faceDetected ||
        _searchResult?.faceDetected ||
        this.selectedSearchResult?.faceDetected ||
        null,
    };
    this.applyPlatformCategoriesFromResponse(vendor);

    this.searchResultDetails = (vendor.results || []).map((item: any) =>
      this.mapVendorResultToPlatformCard(item),
    );
    this.searchResultDetailsFiltered = JSON.parse(
      JSON.stringify(this.searchResultDetails),
    );

    const images = this.buildImagesFromVendorPayload(vendor);
    this.applyFaceDetectionToImages(images);

    this.aggregatedNames = Array.isArray(vendor.names) ? [...vendor.names] : [];
    this.aggregatedEmails = this.normalizeVendorEmails(vendor.emails);
    this.aggregatedEmailHints = [];

    if (
      (!this.searchResultDetailsFiltered ||
        this.searchResultDetailsFiltered.length === 0) &&
      (!this.searchResultImages || this.searchResultImages.length === 0)
    ) {
      this.noRecords = true;
    }
    this.scheduleMasonryLayout();
  }

  private mapVendorResultToPlatformCard(item: any): any {
    const status = String(item?.status || '').toLowerCase();
    const platformKey = String(item?.platform || item?.name || 'unknown');
    const displayName = String(item?.name || item?.platform || 'Unknown');
    const profile =
      item?.profile && typeof item.profile === 'object' ? item.profile : {};
    const imageUrl = this.resolvePlatformAvatarUrl({ profile, platform: displayName, platformKey });
    const details: any[] = [];

    if (profile.display_name || profile.name) {
      details.push({
        label: 'Name',
        value: profile.display_name || profile.name,
        type: 'string',
        'osint-search-type': 'name',
      });
    }
    if (item?.profile_url) {
      details.push({
        label: 'Profile Url',
        value: item.profile_url,
        type: 'link',
      });
    }
    Object.entries(profile).forEach(([key, value]) => {
      if (
        [
          'display_name',
          'name',
          'avatar',
          'avatar_full',
          'profile_url',
          'url',
        ].includes(key)
      ) {
        return;
      }
      if (value === undefined || value === null || value === '') return;
      if (typeof value === 'object') return;
      const lower = key.toLowerCase();
      const type =
        typeof value === 'boolean'
          ? 'boolean'
          : typeof value === 'number'
            ? 'number'
            : lower.includes('url') || lower.includes('link')
              ? 'link'
              : 'string';
      details.push({
        label: this.humanizeVendorFieldLabel(key),
        value,
        type,
      });
    });

    return {
      platform: displayName,
      platformKey,
      category: item?.category || this.getPlatformCategory(displayName),
      status,
      notFound: status === 'not_found',
      isUnknown: status === 'unknown',
      emptyAccount: status === 'found' && details.length === 0 && !item?.profile_url,
      icon: createPlatformIconDataUrl(platformKey),
      profile_url: item?.profile_url || null,
      profile,
      imageUrl,
      faceDetected: item?.faceDetected === true,
      entries: details.length
        ? [
          {
            kind: 'profile',
            details,
          },
        ]
        : [],
    };
  }

  /**
   * Build gallery images for cards/collage.
   * Prefer per-source profile.avatar_full / profile.avatar so cards can match by platform.
   * Top-level `avatars[]` is only used for leftovers / unmatched collected URLs.
   */
  private buildImagesFromVendorPayload(vendor: any): SearchImage[] {
    const images: SearchImage[] = [];
    const seen = new Set<string>();
    const faceMap =
      vendor?.faceDetected && typeof vendor.faceDetected === 'object'
        ? vendor.faceDetected
        : this.selectedSearchResult?.faceDetected &&
            typeof this.selectedSearchResult.faceDetected === 'object'
          ? this.selectedSearchResult.faceDetected
          : null;

    const resolveFaceFlag = (src: string, resultItem?: any): boolean => {
      if (resultItem?.faceDetected === true) return true;
      if (resultItem?.faceDetected === false) return false;
      if (!faceMap) return getFaceDetectCache(src) === true;
      if (faceMap[src] === true) return true;
      const normalized = String(src || '').split('?')[0];
      if (normalized && faceMap[normalized] === true) return true;
      return getFaceDetectCache(src) === true;
    };

    const pushImage = (src: string, platform: string, resultItem?: any) => {
      const url = String(src || '').trim();
      if (!url || seen.has(url)) return;
      if (!(url.startsWith('http') || url.startsWith('data:image/'))) return;
      seen.add(url);
      const faceDetected = resolveFaceFlag(url, resultItem);
      if (faceDetected) {
        setFaceDetectCache(url, true);
      }
      images.push({
        platform,
        src: url,
        name: platform,
        height: images.length === 0 ? 200 : 150,
        icon: createPlatformIconDataUrl(platform),
        detectUrl: url,
        faceDetected,
      });
    };

    // 1) Bind images to their source first (avatar_full || avatar).
    (Array.isArray(vendor?.results) ? vendor.results : []).forEach(
      (item: any) => {
        if (String(item?.status || '').toLowerCase() !== 'found') return;
        const platform = String(item?.name || item?.platform || 'Platform');
        const avatar = this.resolvePlatformAvatarUrl({
          profile: item?.profile,
          platform,
          platformKey: item?.platform,
        });
        if (avatar) {
          pushImage(avatar, platform, item);
        }
      },
    );

    // 2) Add remaining collected avatars[] that were not tied to a source yet.
    const avatarPool = Array.isArray(vendor?.avatars) ? vendor.avatars : [];
    avatarPool.forEach((avatar: string, index: number) => {
      const url = String(avatar || '').trim();
      if (!url || seen.has(url)) return;

      // Try to attach leftover URL to a found source whose profile avatar matches.
      const matchedResult = (Array.isArray(vendor?.results) ? vendor.results : []).find(
        (item: any) => {
          if (String(item?.status || '').toLowerCase() !== 'found') return false;
          const profileAvatar = this.resolvePlatformAvatarUrl({
            profile: item?.profile,
            platform: item?.name,
            platformKey: item?.platform,
          });
          return profileAvatar && this.normalizeImageUrl(profileAvatar) === this.normalizeImageUrl(url);
        },
      );

      if (matchedResult) {
        pushImage(
          url,
          String(matchedResult?.name || matchedResult?.platform || 'Platform'),
          matchedResult,
        );
        return;
      }

      pushImage(url, index === 0 ? 'Avatar' : `Avatar ${index + 1}`);
    });

    return images;
  }

  /** Prefer avatar_full, then avatar, from a platform/result card. */
  private resolvePlatformAvatarUrl(platformResult: any): string | null {
    if (!platformResult || typeof platformResult !== 'object') {
      return null;
    }
    const profile =
      platformResult.profile && typeof platformResult.profile === 'object'
        ? platformResult.profile
        : {};
    const candidates = [
      platformResult.imageUrl,
      profile.avatar_full,
      profile.avatar,
      platformResult.avatar_full,
      platformResult.avatar,
    ];
    for (const candidate of candidates) {
      const url = String(candidate || '').trim();
      if (url.startsWith('http') || url.startsWith('data:image/')) {
        return url;
      }
    }
    return null;
  }

  private normalizeImageUrl(url: string): string {
    return String(url || '')
      .trim()
      .split('?')[0]
      .toLowerCase();
  }

  private buildSearchImageFromPlatformCard(
    platformResult: any,
  ): SearchImage | undefined {
    const src = this.resolvePlatformAvatarUrl(platformResult);
    if (!src) {
      return undefined;
    }
    const platform = String(
      platformResult?.platform ||
      platformResult?.platformKey ||
      platformResult?.name ||
      'Platform',
    );
    const faceDetected =
      platformResult?.faceDetected === true ||
      getFaceDetectCache(src) === true;
    if (faceDetected) {
      setFaceDetectCache(src, true);
    }
    return {
      platform,
      src,
      name: platform,
      height: 150,
      icon: createPlatformIconDataUrl(
        platformResult?.platformKey || platform,
      ),
      detectUrl: src,
      faceDetected,
    };
  }

  private normalizeVendorEmails(emails: any): string[] {
    if (!Array.isArray(emails)) return [];
    const out: string[] = [];
    for (const entry of emails) {
      if (typeof entry === 'string' && entry.trim()) {
        out.push(entry.trim());
        continue;
      }
      if (entry && typeof entry === 'object') {
        const value =
          entry.email || entry.address || entry.value || entry.mail || null;
        if (typeof value === 'string' && value.trim()) {
          out.push(value.trim());
        }
      }
    }
    return Array.from(new Set(out));
  }

  private humanizeVendorFieldLabel(key: string): string {
    return String(key || '')
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  resolvePlatformCategory(platformResult: any): string {
    if (platformResult?.category) {
      return String(platformResult.category);
    }
    return this.getPlatformCategory(platformResult?.platform);
  }

  private getSourceKeysWithNoAccount(searchResult: any): Set<string> {
    const hiddenSources = new Set<string>();
    const sources = searchResult?.data_list?.[0]?.sources;

    if (!sources || typeof sources !== 'object') {
      return hiddenSources;
    }

    Object.entries(sources).forEach(([sourceKey, sourceValue]) => {
      if (
        sourceValue &&
        typeof sourceValue === 'object' &&
        (sourceValue as any).has_account === false
      ) {
        hiddenSources.add(this.normalizePlatformNameForLookup(sourceKey));
      }
    });

    return hiddenSources;
  }

  private normalizePlatformNameForLookup(value: any): string {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');
  }

  onSearchTermChange() {
    const searchTermLower = this.searchTerm.toLowerCase();

    if (!searchTermLower) {
      this.searchResultDetailsFiltered = JSON.parse(
        JSON.stringify(this.searchResultDetails),
      );
    } else {
      this.searchResultDetailsFiltered = this.searchResultDetails
        .map((platform) => {
          const topLevelMatch = this.matchesTopLevel(platform, searchTermLower);

          const matchingEntries = (platform.entries ?? []).filter(
            (entry: any) => this.matchesEntry(entry, searchTermLower),
          );

          if (topLevelMatch || matchingEntries.length > 0) {
            return {
              ...platform,
              entries:
                matchingEntries.length > 0 ? matchingEntries : platform.entries,
            };
          }

          return null;
        })
        .filter((platform) => platform !== null);
    }
    this.scheduleMasonryLayout();
  }

  onExportOptionClick(exportFormat: string) {
    this.exportSearchResult(exportFormat);
  }

  onPrintableOptionClick(): void {
    const routeId =
      this.selectedSearchResult?.portal_job_id ||
      this.selectedSearchResult?.job_id ||
      this.searchId ||
      '';
    if (!routeId || !this.selectedSearchResult) {
      return;
    }

    const searchResult = {
      ...this.selectedSearchResult,
      done: true,
      osint_search_id:
        this.searchId || this.selectedSearchResult.osint_search_id,
    };

    openOsintPrintableDialog(this.dialog, {
      searchResult,
      searchId:
        this.searchId || this.selectedSearchResult.osint_search_id,
      routeId,
      searchType: 'username',
    });
  }

  exportSearchResult(format: string) {
    this.osintSearchService
      .exportSearchResult('username', this.searchId, format)
      .subscribe({
        next: (response) => {
          this.saveFile(response?.data);
        },
        error: (error) => {
          console.error(error);
        },
      });
  }

  saveFile(fileInfo: any) {
    const { fileName, mimeType, base64 } = fileInfo;

    const linkSource = `data:${mimeType};base64,${base64}`;
    const saveLink = document.createElement('a');

    saveLink.href = linkSource;
    saveLink.download = fileName;
    saveLink.click();
  }

  onUpdateNotesButtonClick(): void {
    const resultId = this.selectedSearchResult?.result_id;

    if (!this.notes.trim() || !resultId) {
      this.snackBar.open(
        'Please enter notes and ensure result ID is available.',
        'Close',
        {
          duration: 3000,
          panelClass: ['snackbar-error'],
        },
      );
      return;
    }

    const payload = {
      notes: this.notes.trim(),
    };

    this.osintSearchService.updateUsernameNotes(resultId, payload).subscribe({
      next: () => {
        this.snackBar.open('Notes updated successfully!', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-success'],
        });
      },
      error: () => {
        this.snackBar.open('Failed to update notes.', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-error'],
        });
      },
    });
  }

  private matchesTopLevel(platform: any, searchTerm: string): boolean {
    return Object.entries(platform)
      .filter(([key]) => key !== 'entries')
      .some(
        ([key, value]) =>
          this.includesSearchTerm(key, searchTerm) ||
          this.includesSearchTerm(value, searchTerm),
      );
  }

  private matchesEntry(entry: any, searchTerm: string): boolean {
    if (!entry) {
      return false;
    }

    const labelMatch = this.includesSearchTerm(entry.label, searchTerm);
    const kindMatch = this.includesSearchTerm(entry.kind, searchTerm);

    const detailsMatch = Array.isArray(entry.details)
      ? entry.details.some(
        (detail: any) =>
          this.includesSearchTerm(detail.label, searchTerm) ||
          this.includesSearchTerm(detail.type, searchTerm) ||
          this.includesSearchTerm(detail.value, searchTerm),
      )
      : false;

    return labelMatch || kindMatch || detailsMatch;
  }

  private includesSearchTerm(value: any, searchTerm: string): boolean {
    if (!searchTerm) {
      return false;
    }

    if (value === undefined || value === null) {
      return false;
    }

    if (typeof value === 'string') {
      return value.toLowerCase().includes(searchTerm);
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value).toLowerCase().includes(searchTerm);
    }

    if (Array.isArray(value)) {
      return value.some((item) => this.includesSearchTerm(item, searchTerm));
    }

    if (typeof value === 'object') {
      return Object.values(value).some((item) =>
        this.includesSearchTerm(item, searchTerm),
      );
    }

    return false;
  }

  openImageModal(image: SearchImage): void {
    this.selectedImage = image;
    document.body.style.overflow = 'hidden';
  }

  closeImageModal(): void {
    this.selectedImage = null;
    document.body.style.overflow = '';
  }

  @HostListener('document:keydown', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (event.key !== 'Escape') {
      return;
    }
    if (this.showFoundDrillModal) {
      this.closeFoundDrillModal();
      return;
    }
    if (this.showNotFoundDrillModal) {
      this.closeNotFoundDrillModal();
      return;
    }
    if (this.showUnknownDrillModal) {
      this.closeUnknownDrillModal();
      return;
    }
    if (this.showNamesDrillModal) {
      this.closeNamesDrillModal();
      return;
    }
    if (this.showPhotosDrillModal) {
      this.closePhotosDrillModal();
      return;
    }
    if (this.showEmailsDrillModal) {
      this.closeEmailsDrillModal();
      return;
    }
    if (this.showEmailHintsDrillModal) {
      this.closeEmailHintsDrillModal();
      return;
    }
    if (this.selectedImage) {
      this.closeImageModal();
    }
  }

  // Update aggregated data from API response
  private updateAggregatedData(): void {
    // Use name and email arrays directly from API response
    this.aggregatedNames = Array.isArray(this.selectedSearchResult?.name)
      ? this.selectedSearchResult.name
      : [];
    this.aggregatedEmails = Array.isArray(this.selectedSearchResult?.email)
      ? this.selectedSearchResult.email
      : [];
    this.aggregatedEmailHints = Array.isArray(
      this.selectedSearchResult?.email_hint,
    )
      ? this.selectedSearchResult.email_hint
      : [];
  }

  // Format list values for display
  formatListValue(value: any[]): string {
    if (!Array.isArray(value)) {
      return '';
    }
    if (value.length === 0) {
      return '[]';
    }
    return this.utilsService.joinDisplayListItems(
      value.map((item) => this.utilsService.formatListItemForDisplay(item)),
    );
  }

  getDisplayListItems(detail: any): string[] {
    const values = Array.isArray(detail?.value) ? detail.value : [];
    const label = String(detail?.label || '')
      .trim()
      .toLowerCase();

    return values
      .map((item: any) => this.utilsService.formatListItemForDisplay(item, label))
      .filter((item: string) => item.length > 0);
  }

  /** Full text for truncated detail values shown on hover. */
  getDetailTooltipText(detail: any): string | null {
    if (!detail || this.isEmptyValue(detail.value)) {
      return null;
    }

    let text = '';
    switch (detail.type) {
      case 'list':
      case 'array':
        text = this.utilsService.joinDisplayListItems(this.getDisplayListItems(detail));
        break;
      case 'boolean':
        text = detail.value ? 'Yes' : 'No';
        break;
      case 'datetime':
        text = this.utilsService.formatDateTime(detail.value);
        break;
      case 'date':
        text = this.formatDateValue(detail.value);
        break;
      case 'object':
      case 'dict':
        text = this.formatObjectValue(detail.value);
        break;
      case 'link':
      case 'number':
      default:
        text = this.utilsService.formatDisplayValue(detail.value);
        break;
    }

    const trimmed = String(text || '').trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private formatListItemForDisplay(item: any, detailLabel: string): string {
    return this.utilsService.formatListItemForDisplay(item, detailLabel);
  }

  // Format object values for display
  formatObjectValue(value: any): string {
    return this.utilsService.formatKeyValueObject(value);
  }

  // Format date values (YYYY-MM-DD format)
  formatDateValue(value: any): string {
    if (!value) {
      return '';
    }
    try {
      // Try to format as date using utils service if it's a datetime
      if (this.utilsService.isIsoDate(value)) {
        return this.utilsService.formatDateTime(value, 'shortDate');
      }
      // For YYYY-MM-DD format, parse and format
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return String(value);
      }
      return date.toLocaleDateString();
    } catch {
      return String(value);
    }
  }

  // Check if value is empty (for lists and objects)
  isEmptyValue(value: any): boolean {
    if (value === null || value === undefined) {
      return true;
    }
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    if (typeof value === 'object') {
      return Object.keys(value).length === 0;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length === 0 || this.isBlankPlaceholder(trimmed);
    }
    return false;
  }

  /** API placeholders such as "()" are blank and should not render. */
  private isBlankPlaceholder(value: any): boolean {
    return typeof value === 'string' && value.trim() === '()';
  }

  // Get object keys for dict display
  getObjectKeys(obj: any): string[] {
    if (!obj || typeof obj !== 'object') {
      return [];
    }
    return Object.keys(obj);
  }

  // Format key for display (e.g., "DateTimeOriginal" -> "Date Time Original")
  formatDictKey(key: string): string {
    if (!key || typeof key !== 'string') {
      return key;
    }
    // Add space before capital letters (except the first one)
    return key.replace(/([A-Z])/g, ' $1').trim();
  }

  // Navigate to search page and auto-start search
  navigateToSearch(searchType: string | undefined, searchValue: any): void {
    if (!searchType || searchValue === null || searchValue === undefined) {
      return;
    }

    // Only allow navigation for email and username search types
    const allowedTypes = ['email', 'username'];
    if (!allowedTypes.includes(searchType.toLowerCase())) {
      return;
    }

    // Extract the actual value if it's an array (for dict/list types)
    let actualValue = searchValue;
    if (Array.isArray(searchValue)) {
      if (searchValue.length === 0) {
        return;
      }
      // For arrays, take the first non-empty value
      actualValue =
        searchValue.find((v) => v && String(v).trim()) || searchValue[0];
    } else if (typeof searchValue === 'object') {
      // For objects, try to extract a meaningful value
      if ('value' in searchValue) {
        actualValue = searchValue.value;
      } else {
        // Try to get the first string value from the object
        const stringValue = Object.values(searchValue).find(
          (v) => typeof v === 'string' && v.trim(),
        );
        if (stringValue) {
          actualValue = stringValue;
        } else {
          return;
        }
      }
    }

    // Convert to string and trim
    const searchQuery = String(actualValue).trim();
    if (!searchQuery || searchQuery === 'N/A') {
      return;
    }

    // Map search types to routes (only email and username)
    const routeMap: Record<string, string> = {
      email: '/osint-email-search',
      username: '/osint-username-search',
    };

    const route = routeMap[searchType.toLowerCase()];
    if (!route) {
      console.warn(`Unknown search type: ${searchType}`);
      return;
    }

    // Navigate with state to auto-start search
    this.router.navigate([route], {
      state: { autoSearch: true, searchQuery },
    });
  }

  // Check if detail has osint-search-type
  hasOsintSearchType(detail: any): boolean {
    return detail && detail['osint-search-type'];
  }

  // Get osint-search-type from detail
  getOsintSearchType(detail: any): string | undefined {
    return detail?.['osint-search-type'];
  }

  // Check if value is valid for searching (not falsy, not 'N/A', not empty)
  isValidSearchValue(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length > 0 && trimmed !== 'N/A' && trimmed !== '{}';
    }
    if (Array.isArray(value)) {
      return value.length > 0 && value.some((v) => this.isValidSearchValue(v));
    }
    if (typeof value === 'object') {
      return Object.keys(value).length > 0;
    }
    return Boolean(value);
  }

  // Check if detail should show search icon (has osint-search-type AND valid value AND is email/username)
  shouldShowSearchIcon(detail: any): boolean {
    if (deniesFieldSearchIcon(detail)) return false;
    if (
      !this.hasOsintSearchType(detail) ||
      !this.isValidSearchValue(detail.value)
    ) {
      return false;
    }
    const searchType = this.getOsintSearchType(detail);
    // Only show icon for email and username search types
    return (
      searchType?.toLowerCase() === 'email' ||
      searchType?.toLowerCase() === 'username'
    );
  }

  shouldShowRowSearchIcon(detail: any): boolean {
    return this.shouldShowSearchIcon(detail) && !this.isMultiItemSearchableList(detail);
  }

  shouldShowSearchIconForItem(detail: any, item: any): boolean {
    return this.shouldShowSearchIcon({ ...detail, value: item });
  }

  private isMultiItemSearchableList(detail: any): boolean {
    return Array.isArray(detail?.value) && this.getDisplayListItems(detail).length > 1;
  }

  /** Load the same Google web-search URL that CSE's "Search on Google" link uses. */
  initGoogleResults(): void {
    const value = this.selectedSearchResult?.searched_username;
    if (!value) {
      this.clearGoogleResults();
      return;
    }

    const variants = this.googleOsintQueryBuilder.buildVariants(
      'username',
      value,
    );
    this.googleQueryVariants = variants;
    if (!variants.length) {
      this.clearGoogleResults();
      return;
    }

    this.applyGoogleQuery(variants[0], {
      type: 'username',
      value,
      queries: variants.map((variant) => variant.query),
    });
  }

  selectGoogleQuery(variant: GoogleOsintQueryVariant): void {
    if (!variant?.query || this.selectedGoogleQueryId === variant.id) {
      return;
    }
    this.applyGoogleQuery(variant);
  }

  private applyGoogleQuery(
    variant: GoogleOsintQueryVariant,
    context?: { type?: string; value?: string; queries?: string[] },
  ): void {
    this.selectedGoogleQueryId = variant.id;
    const view = this.googleCseService.getEmbeddedSearch(variant.query, context);
    this.googleResultsUrl = view?.embedUrl ?? null;
    this.googleSearchHref = view?.href ?? null;
  }

  private clearGoogleResults(): void {
    this.googleQueryVariants = [];
    this.selectedGoogleQueryId = null;
    this.googleResultsUrl = null;
    this.googleSearchHref = null;
  }
}
